import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { Typography, List, ListItem, Button, TextField, CircularProgress, InputAdornment, IconButton, Accordion, AccordionSummary, AccordionDetails } from '@material-ui/core';
import { PropertyPin } from '../Models/Property';
import { baseEndpoint } from '../App';
import { Neighborhood } from '../Models/Neighborhood';
import SearchIcon from '@material-ui/icons/Search';
import ClearIcon from '@material-ui/icons/Clear';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { Loader } from '@googlemaps/js-api-loader';
const config = require("../env.json")

const INITIAL_MAP_CENTER = { lat: 40.766632, lng: -73.977211 };
const INITIAL_MAP_ZOOM = 12;

function debounce<F extends (...args: any[]) => any>(func: F, waitFor: number) {
  let timeout: ReturnType<typeof setTimeout> | null = null;

  return (...args: Parameters<F>): void => {
    if (timeout !== null) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(() => func(...args), waitFor);
  };
}

interface LoadingState {
  initial: boolean;
  updating: boolean;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      flexGrow: 1,
      marginTop: "10px",
      display: "flex"
    },
    menuButton: {
      marginRight: theme.spacing(2),
    },
    title: {
      flexGrow: 1,
    },
    input: {
      backgroundColor: "white",
      '& label.Mui-focused': {
        color: '#0404fc',
        borderWidth: "3px"
      },
      '& .MuiInput-underline:after': {
        borderBottomColor: '#0404fc',
        borderWidth: "3px"
      },
      '& .MuiOutlinedInput-root': {
        '& fieldset': {
          borderColor: '#0404fc',
          borderWidth: "3px"
        },
        '&:hover fieldset': {
          borderColor: '#0404fc',
          borderWidth: "3px"
        },
        '&.Mui-focused fieldset': {
          borderColor: '#0404fc',
          borderWidth: "3px"
        },
      },
    },
    loadingOverlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(255, 255, 255, 0.8)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
    },
    updateIndicator: {
      position: 'absolute',
      top: '20px',
      left: '50%',
      transform: 'translateX(-50%)',
      background: 'white',
      padding: '8px 16px',
      borderRadius: '20px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      zIndex: 1000,
    }
  }),
);

export const loadMapApi = () => {
  const mapsUrl = "https://maps.googleapis.com/maps/api/js?key=" + config.GOOGLE_API_KEY + "&libraries=places&languages=no&region=NO&v=quarterly"
  const scripts = document.getElementsByTagName('script');
  for (let i = 0; i < scripts.length; i++) {
    if (scripts[i].src.indexOf(mapsUrl) === 0) {
      return scripts[i];
    }
  }

  const googleMapsScript = document.createElement('script');
  googleMapsScript.src = mapsUrl;
  googleMapsScript.async = true;
  googleMapsScript.defer = true;
  window.document.body.appendChild(googleMapsScript);

  return googleMapsScript;
}


type GoogleLatLng = google.maps.LatLng;
type GoogleMap = google.maps.Map;

export default function PropertyList(props: any) {
  const classes = useStyles();
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const markersRef = useRef<Map<number, google.maps.Marker>>(new Map());
  const [visibleMarkerIds, setVisibleMarkerIds] = useState<Set<number>>(new Set());
  const [map, setMap] = useState<GoogleMap>();
  const [neighborhoods, setNeighborhoods] = useState<any[]>([] as any[]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [expandedNeighborhood, setExpandedNeighborhood] = useState<string | null>(null)
  const [pins, setPins] = useState<PropertyPin[]>([] as PropertyPin[]);
  const [highlightedMarker, setHighlightedMarker] = useState<google.maps.Marker | null>(null);
  const [filteredPins, setFilteredPins] = useState<PropertyPin[]>([] as PropertyPin[]);
  const [noResults, setNoResults] = useState(false);


  const [loading, setLoading] = useState<LoadingState>({
    initial: true,
    updating: false
  });

  useEffect(() => {
    if (!map) {
      const loader = new Loader({
        apiKey: config.GOOGLE_API_KEY,
        version: "quarterly",
      });

      loader.load().then(async () => {
        setScriptLoaded(true);
        // Don't set initial loading until after map is initialized
        initMap(new google.maps.LatLng(40.766632, -73.977211));
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);
  useEffect(() => {
    if (map && markersRef.current.size === 0) {
      fetchPins();
      fetchNeighborhoods();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);

  // Set up idle listener when map and pins are ready
  useEffect(() => {
    if (!map || pins.length === 0) return;

    const updateMarkers = debounce(() => {
      // Use filtered pins if search is active, otherwise use all pins
      const pinsToUse = searchTerm !== "" ? filteredPins : pins;
      if (pinsToUse.length > 0) {
        setLoading(prev => ({ ...prev, updating: true }));
        createMarkers(pinsToUse);
        setLoading(prev => ({ ...prev, updating: false }));
      }
    }, 300);

    google.maps.event.addListener(map, 'idle', updateMarkers);

    // Cleanup: remove listener when component unmounts or dependencies change
    return () => {
      google.maps.event.clearListeners(map, 'idle');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, pins, filteredPins, searchTerm]);

  const initMap = (address: GoogleLatLng) => {
    if (ref.current) {
      var newMap = new google.maps.Map(ref.current, {
        zoom: 12,
        center: address,
        mapTypeControl: false,
        streetViewControl: false,
        rotateControl: false,
        scaleControl: true,
        fullscreenControl: false,
        panControl: false,
        zoomControl: true,
        gestureHandling: 'cooperative',
        mapTypeId: google.maps.MapTypeId.ROADMAP,
        draggableCursor: 'pointer',
        styles: [
          {
            featureType: "poi",
            stylers: [
              { visibility: "off" }
            ]
          },
          {
            featureType: "transit",
            stylers: [{ visibility: "off" }],
          },
        ]
      })
      setMap(newMap);
    }
  }

  async function fetchPins() {
    try {
      const response = await fetch(baseEndpoint + 'Properties/pins/list');
      if (response.ok) {
        const pinData = await response.json() as PropertyPin[];
        setPins(pinData);
        requestAnimationFrame(() => {
          createMarkers(pinData);
        });
      }
    } catch (ex) {
      console.error('Error fetching pins:', ex);
    } finally {
      setLoading(prev => ({ ...prev, initial: false }));
    }
  }

  function createMarkers(propertyPins: PropertyPin[]) {
    if (!map) return;
  
    setLoading(prev => ({ ...prev, updating: true }));
  
    const bounds = map.getBounds();
    
    // Fast bounds check without creating LatLng objects
    let visiblePins: PropertyPin[];
    if (bounds) {
      const ne = bounds.getNorthEast();
      const sw = bounds.getSouthWest();
      const minLat = sw.lat();
      const maxLat = ne.lat();
      const minLng = sw.lng();
      const maxLng = ne.lng();
      
      // Direct lat/lng comparison - much faster than creating LatLng objects
      visiblePins = propertyPins.filter(p => 
        p.lat >= minLat && p.lat <= maxLat && 
        p.lng >= minLng && p.lng <= maxLng
      );
    } else {
      visiblePins = propertyPins;
    }
  
    // Track which markers should be visible
    const newVisibleIds = new Set<number>();
    const currentMarkers = markersRef.current;
    const markersToHide: google.maps.Marker[] = [];
  
    // Only process visible pins
    visiblePins.forEach(p => {
      newVisibleIds.add(p.internalId);
    
      let marker = currentMarkers.get(p.internalId);
  
      if (!marker) {
        // Only create marker if it doesn't exist
        marker = new google.maps.Marker({
          position: { lat: p.lat, lng: p.lng },
          icon: 'green-pin.png',
          label: {
            text: Math.round(p.calcScore).toString(),
            color: '#0404fc',
            fontSize: "9px",
            fontWeight: "bold"
          },
          title: `${p.address} ${p.city}, ${p.state} ${p.zip}`,
        });
  
        marker.addListener('click', () => {
          window.location.href = `/property/${p.internalId}`;
        });
  
        currentMarkers.set(p.internalId, marker);
      }
  
      // Only call setMap if marker wasn't already visible
      if (!visibleMarkerIds.has(p.internalId)) {
        marker.setMap(map);
      }
    });
  
    // Hide markers that are no longer visible (only iterate through previously visible)
    visibleMarkerIds.forEach(id => {
      if (!newVisibleIds.has(id)) {
        const marker = currentMarkers.get(id);
        if (marker) {
          markersToHide.push(marker);
        }
      }
    });
  
    // Batch state updates
    markersToHide.forEach(marker => marker.setMap(null));
    setVisibleMarkerIds(newVisibleIds);
    setLoading(prev => ({ ...prev, updating: false }));
  }

  function handlePropertyHover(pin: PropertyPin, isEntering: boolean) {
    if (highlightedMarker) {
      highlightedMarker.setMap(null);
      setHighlightedMarker(null);
    }

    if (isEntering && map) {
      const marker = new google.maps.Marker({
        position: { lat: pin.lat, lng: pin.lng },
        map: map,
        label: {
          text: Math.round(pin.calcScore).toString(),
          color: '#17d703',    // Green text
          fontSize: "11px",     // Slightly larger font
          fontWeight: "bold"
        },
        icon: {
          url: 'blue-pin.png',
          scaledSize: new google.maps.Size(35, 35),  // Normal size is probably 25,25
        },
        zIndex: 2000,
      });
      setHighlightedMarker(marker);
    }
  }

  async function fetchNeighborhoods() {
    try {
      await fetch(baseEndpoint + 'neighborhoods').then(async (response) => {
        if (response.ok) {
          var r = await response.json() as Neighborhood[];
          setNeighborhoods(r);
        }
      })
    }
    catch (ex) {

    }
  }

  const handleChangeSearchTerm = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  }

  const search = () => {
    if (searchTerm !== "") {
      // Clear any highlighted marker
      if (highlightedMarker) {
        highlightedMarker.setMap(null);
        setHighlightedMarker(null);
      }

      // Filter pins based on search term
      const filtered = pins.filter(p =>
        p.address.toUpperCase().startsWith(searchTerm.toUpperCase()) ||
        p.city.toUpperCase().includes(searchTerm.toUpperCase()) ||
        (p.masterAddress && p.masterAddress.toUpperCase().startsWith(searchTerm.toUpperCase()))
      );

      setFilteredPins(filtered);
      setNoResults(filtered.length === 0);
      createMarkers(filtered);

      if (filtered.length === 1) {
        map?.setCenter({ lat: filtered[0].lat, lng: filtered[0].lng });
        map?.setZoom(15);
      } else if (filtered.length > 1) {
        const bounds = new google.maps.LatLngBounds();
        filtered.forEach(p => bounds.extend({ lat: p.lat, lng: p.lng }));
        map?.fitBounds(bounds);
      }
    } else {
      setFilteredPins(pins);
      setNoResults(false);
      createMarkers(pins);
    }
  };

  // Update clear function
  const clear = () => {
    setSearchTerm("");
    setFilteredPins([] as PropertyPin[]);
    setNoResults(false);
    createMarkers(pins);

    if (map) {
      map.setCenter(INITIAL_MAP_CENTER);
      map.setZoom(INITIAL_MAP_ZOOM);
    }
  };

  const handleExpandNeighborhood = (neighborhood: Neighborhood) => {
    if (expandedNeighborhood === neighborhood.name) {
      setExpandedNeighborhood(null)
      if (map) {
        map.setCenter(INITIAL_MAP_CENTER);
        map.setZoom(INITIAL_MAP_ZOOM);
      }
    }
    else {
      setExpandedNeighborhood(neighborhood.name);
      if (map) {
        map.setCenter({ lat: Number(neighborhood.latCenter), lng: Number(neighborhood.lngCenter) });
        map.setZoom(15);
      }
    }
  }

  return (
    <div className={classes.root}>
      <div style={{ width: "100%" }}>
        <div style={{ backgroundColor: "#0404fc", borderWidth: "5px", height: "29px", marginRight: "17px", marginLeft: "17px" }} >
          <Typography style={{ textAlign: "left", color: "white", paddingLeft: "10px" }} variant="h6">Property Search</Typography>
        </div>
        <div style={{ display: "flex" }}>
          <div style={{ width: "38%" }}>
            <div style={{ paddingLeft: "17px" }}>
              <form onSubmit={(e: any) => {
                e.preventDefault();
                search();

              }} style={{ marginTop: "10px", marginLeft: "50px" }}>
                <TextField
                  size="small"
                  style={{ width: "100%" }}
                  className={classes.input}
                  id="firstName"
                  label="Street Number"
                  variant="outlined"
                  value={searchTerm}
                  onChange={handleChangeSearchTerm}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><IconButton type="submit" onClick={search} style={{ padding: "2px" }}><SearchIcon /></IconButton></InputAdornment>,
                    endAdornment: searchTerm !== "" ? <InputAdornment position="end"><IconButton onClick={clear} style={{ padding: "2px" }}><ClearIcon /></IconButton></InputAdornment> : null
                  }}
                />
              </form>

            </div>
            <div style={{ paddingLeft: "72px", height: "56vh", maxHeight: "56vh", overflow: "auto" }}>
              {noResults ? (
                <div style={{ height: "60vh" }}>
                  <Typography variant="body1">
                    <strong>No results found for "{searchTerm}"</strong>
                  </Typography>
                </div>
              ) : (
                <List>
                  {neighborhoods.map((n, i) => {
                    // Filter pins by neighborhood and search term
                    const neighborhoodPins = (filteredPins.length > 0 ? filteredPins : pins)
                      .filter(p => p.neighborhoodId === n.internalId);

                    if (neighborhoodPins.length > 0) {
                      return (
                        <ListItem
                          style={{ display: "block", paddingTop: "2px", paddingBottom: "2px" }}
                          key={i}
                        >
                          <Accordion
                            expanded={expandedNeighborhood === n.name}
                            onChange={() => handleExpandNeighborhood(n)}
                          >
                            <AccordionSummary
                              expandIcon={<ExpandMoreIcon />}
                              aria-controls={n.name + "-content"}
                              id={n.name + "-header"}
                            >
                              <Typography>{n.name} ({neighborhoodPins.length})</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                              <List style={{ paddingTop: "0px" }}>
                                {neighborhoodPins.map((p, j) => {
                                  const add = `${p.address}, ${p.city}, ${p.state} ${p.zip}`;
                                  return (
                                    <ListItem
                                      style={{ paddingTop: "1px", paddingBottom: "1px" }}
                                      key={j}
                                    >
                                      <Button
                                        href={'/property/' + p.internalId}
                                        style={{ textTransform: "none" }}
                                        id={p.internalId.toString()}
                                        onMouseEnter={() => handlePropertyHover(p, true)}
                                        onMouseLeave={() => handlePropertyHover(p, false)}
                                      >
                                        <Typography variant="body2">
                                          {add}
                                        </Typography>
                                      </Button>
                                    </ListItem>
                                  );
                                })}
                              </List>
                            </AccordionDetails>
                          </Accordion>
                        </ListItem>
                      );
                    }
                    return null;
                  })}
                </List>
              )}
            </div>
            <div style={{ borderWidth: "3px", borderColor: "#0404fc", borderStyle: "solid", marginLeft: "15px", height: "calc(72vh - 56vh - 50px)" }}>
              <Typography style={{ color: "#0404fc", marginBottom: "5px", textAlign: "left", marginLeft: "10px" }} variant="body2">We are continually analyzing properties and adding more Parslii Scores. Don't see the address you're looking for? Request a Score! It's FREE
                <Button size='small' href='/requestProperty' style={{ backgroundColor: "#17d703", color: "white", marginLeft: "10px" }}>Request a Parslii Score</Button>

              </Typography>
            </div>
          </div>
          <div style={{ width: "62%", height: "72vh" }}>
            <div style={{ height: "100%", margin: "10px 17px", position: 'relative' }}>
              {scriptLoaded && (
                <>
                  <div
                    ref={ref}
                    style={{ height: "100%" }}
                    className="map-container__map"
                  />

                  {/* Initial loading overlay */}
                  {loading.initial && (
                    <div className={classes.loadingOverlay}>
                      <CircularProgress />
                      <p>Loading properties...</p>
                    </div>
                  )}

                  {/* Update indicator */}
                  {loading.updating && (
                    <div className={classes.updateIndicator}>
                      <CircularProgress size={20} />
                      <span>Updating map...</span>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}